import { isProfileLocked } from 'library/helpers/personStatus';
import { billingPathFor, isBillingOnlyProfile } from 'library/helpers/billingContact';
import React, { lazy } from 'react';
import { Navigate, Route } from 'react-router-dom';
const FourZeroFour = lazy(() => import('publicContainer/404'));
const PageNotAuthorized = lazy(() => import('publicContainer/401'));
const NoDashboardAccess = lazy(() => import('publicContainer/NoDashboardAccess'));
const AccountDisabled = lazy(() => import('publicContainer/AccountDisabled'));

/**
 * Does this person hold NO dashboard role whatsoever?
 *
 * Not the same question as "may they see this page", and the difference is the whole point of
 * the branch below. `addPerson` enrols an invitee into the workspace and assigns no role, so a
 * person invited to open a door arrives here with `roles = {}` — which intersects no route's
 * `access` array, so EVERY route renders the 401. They then read "contact your administrator"
 * about a system that is working exactly as designed, and raise a ticket nobody can close.
 *
 * `isPlatformDeveloper` counts as a role: the allowlist flag can arrive before the self-seeded
 * 201 row exists, and a developer in that window is emphatically not an app-only user.
 *
 * @param {Object} person
 * @returns {boolean}
 */
const hasNoDashboardRole = (person) =>
	person?.isPlatformDeveloper !== true && Object.keys(person?.roles || {}).length === 0;

/**
 * RouteRenderer component renders a list of routes based on the user's roles.
 *
 * @param {Object} props - The properties object.
 * @param {Object} props.person - The person object containing user information.
 * @param {Object} props.person.roles - The roles object containing user roles.
 * @param {Array} props.routesList - The list of route objects to be rendered.
 * @param {Object} props.routesList[].access - The array of roles that have access to the route.
 * @param {boolean} props.routesList[].exact - Whether the route should be matched exactly.
 * @param {string} props.routesList[].path - The path of the route.
 * @param {React.Component} props.routesList[].component - The component to be rendered for the route.
 *
 * @returns {JSX.Element} The rendered routes based on the user's roles.
 */
const RouteRenderer = ({ person, routesList }) => {
	// Computed ONCE for the whole route table, not per route: "no roles at all" is a property of
	// the person, and evaluating it inside the map would invite someone to conflate it with the
	// per-route check below.
	const appOnlyUser = hasNoDashboardRole(person);

	// Disabled in this org: every route says so, ahead of any role or developer check.
	if (isProfileLocked(person)) {
		return (
			<>
				{routesList.map((route, index) => (
					<Route key={index} path={route.path} element={<AccountDisabled />} />
				))}
				<Route path='*' key={'404'} element={<FourZeroFour inShell />} />
			</>
		);
	}

	// Billing-only in this org (D48): the Billing page is the only screen. Every other route lands there, so no screen
	// mounts that would ask for data the server refuses this person. A route opts in with `billingContactAccess`.
	if (isBillingOnlyProfile(person)) {
		const home = billingPathFor(person.orgID);
		return (
			<>
				{routesList.map((route, index) =>
					route.billingContactAccess === true ? (
						<Route key={index} exact={route.exact} path={route.path} element={<route.component />} />
					) : (
						<Route key={index} path={route.path} element={<Navigate to={home} replace />} />
					)
				)}
				<Route path='*' key={'404'} element={<Navigate to={home} replace />} />
			</>
		);
	}

	return (
		<>
			{routesList.map((route, index) => {
				// The profile allowlist flag satisfies Developer-gated routes (access includes 201):
				// on first login into a fresh org the roles map predates the self-seeded 201 row.
				const hasRouteAccess =
					(person.roles &&
						Object.keys(person.roles)
							.map(Number)
							.some((role) => route.access.includes(role))) ||
					(person.isPlatformDeveloper === true && route.access.includes(201));
				if (hasRouteAccess) {
					return <Route key={index} exact={route.exact} path={route.path} element={<route.component />} />;
				}
				// Two different denials, two different messages. Someone WITH roles who lacks this
				// one has hit a permission boundary and an administrator can move it — that is the
				// 401. Someone with no roles at all has hit no boundary; they are simply not a
				// dashboard user, and the honest answer is where their access actually is.
				return (
					<Route
						key={index}
						path={route.path}
						element={appOnlyUser ? <NoDashboardAccess /> : <PageNotAuthorized inShell />}
					/>
				);
			})}

			<Route path='*' key={'404'} element={<FourZeroFour inShell />} />
		</>
	);
};

export default RouteRenderer;
